import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback, useContext, forwardRef } from "react";
import classNames from "classnames";
import FocusTrap from 'focus-trap-react';
import { throttle } from "underscore";

import Icon, { COLORS, ICONS } from "../icon";
import ModalPortal from "./modal-portal";
import Switch from "../switch/switch";
import { FieldLabel } from "../field/field";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { ThemeContext } from "components/live-event/theme-context";
import { EPaletteModes } from "types/theme-packs";
import { useTranslate } from "i18n/useTranslationModules";

import './modal.scss';
import "./ev-modal.scss";
import { mergeRefs } from "utils/merge-refs";

interface IModalBodyWrapper {
	useAdditionalWrapper: boolean;
}

const ModalBodyWrapper: React.FC<React.PropsWithChildren<IModalBodyWrapper>> = ({ useAdditionalWrapper, children }) => {
	if (useAdditionalWrapper) {
		return (
			<div className="additional-modal-body-wrapper">
				{children}
			</div>
		);
	}
	return (
		<>
			{children}
		</>
	);
};

export type ModalProps = {
	open: boolean;
	children: any;
	title?: string;
	ariaLabel?: string;
	ariaDescription?: string;
	titleClassName?: string;
	onRequestClose: () => void;
	header?: any;
	footer?: any;
	cancellable: boolean;
	closeable: boolean;
	size?: 'normal' | 'medium' | 'large' | 'small' | 'desktop' | 'tablet' | 'mobile' | 'wide' | 'fullscreen';
	subtitle?: string;
	closeOnEscape?: boolean;
	className?: string;
	noPadding?: boolean;
	noSidePadding?: boolean;
	hideHeader?: boolean;
	trapFocus?: boolean;
	inertBackground?: boolean;
	focusTrapOptions?: { [key: string]: boolean };
	showOverflow?: boolean;
	scrollOverflow?: boolean;
	padTitle?: boolean;
	eventPreview?: boolean;
	scrollBodyToBottom?: boolean; // Added for create breakout rooms session modal
	topRightToggle?: boolean;	// Added for create breakout rooms session modal
	onToggleClicked?: (value: any, on: boolean) => void; // Added for create breakout rooms session modal
	toggleLabel?: string // Added for create breakout rooms session modal
	toggleValue?: boolean // Added for create breakout rooms session modal
	throttleTime?: number;
	onBodyScroll?: (...args: any[]) => void;
	onBodyScrollToBottom?: () => void;
	noCloseOnEscape?: boolean;
	closeByX?: boolean;
	useAdditionalWrapper?: boolean;
	addModalPadding?: boolean;
	portalClassName?: string;
	onTransitionedClosed?: () => void;
	onTransitionedOpened?: () => void;
	topRightToggleTooltipText?: string;
	/** isAdmin will prevent event font family from overwriting the modal font family */
	isAdmin?: boolean;
	id?: string;
};

const ModalComponent = forwardRef<HTMLDivElement, ModalProps>((props: ModalProps, bodyRef): JSX.Element => {
	const {
		addModalPadding = false,
		ariaLabel,
		ariaDescription,
		cancellable,
		children,
		className,
		closeable,
		closeByX = false,
		eventPreview,
		focusTrapOptions = { allowOutsideClick: true },
		footer,
		header,
		hideHeader = false,
		noCloseOnEscape = false,
		noPadding = false,
		noSidePadding = false,
		onToggleClicked,
		onBodyScroll,
		onBodyScrollToBottom,
		open,
		onRequestClose,
		onTransitionedClosed,
		onTransitionedOpened,
		padTitle = true,
		portalClassName = '',
		scrollBodyToBottom,
		size = "normal",
		showOverflow = false,
		scrollOverflow = false,
		subtitle,
		throttleTime = 100,
		title = '',
		titleClassName,
		trapFocus = true,
		inertBackground = false,
		toggleLabel,
		toggleValue = false,
		topRightToggle,
		useAdditionalWrapper = false,
		topRightToggleTooltipText = '',
		// initializing true because the majority of our modals are in admin
		isAdmin = true,
		id
	} = props;

	const aDropdownIsOpen = useTypedSelector(state => state.CreateEventReducer.aDropdownIsOpen);
	const { t } = useTranslate('session');
	const [isOpen, setIsOpen] = useState(false);
	const [openClass, setOpenClass] = useState("closed");
	const transitionComplete = useRef(onTransitionedClosed);
	const transitionCompletedOpen = useRef(onTransitionedOpened);
	const close = useRef(onRequestClose);
	const modalBodyRef = useRef<HTMLDivElement>(null);

	const [theme] = useContext(ThemeContext);
	const darkMode = theme === EPaletteModes.Dark;

	useEffect(() => {
		// Added this for the Breakout Rooms modal. When adding new rooms we flip the "scrollBodyToBottom" prop which
		// fires this to auto scroll smoothly to the bottom of the rooms list to bring the new one into view
		if (scrollBodyToBottom !== undefined) {
			setTimeout(() => {
				modalBodyRef.current?.scrollBy({ top: modalBodyRef.current.scrollHeight, behavior: "smooth" });
			}, 100);
		}
	}, [scrollBodyToBottom]);

	useEffect(() => {
		// prevent the overflow from being toggled on or off on every render
		// which causes the scroll bar on the right to blink in and out when the parent state changes
		close.current = onRequestClose;
	}, [onRequestClose]);

	useEffect(() => {
		const escape = (e: any) => {
			if (e.key === 'Escape') {
				close.current();
			}
		};

		if (open) {
			setIsOpen(true);
			setTimeout(() => setOpenClass("open"));
			if (!noCloseOnEscape) {
				window.addEventListener('keyup', escape);
			}
		} else {
			setOpenClass("closed");
		}

		return () => {
			window.removeEventListener('keyup', escape);
			setTimeout(() => {
				if (typeof document !== 'undefined') {
					document.body.style.overflow = '';
				}
			}, 100);
		};
	}, [open, noCloseOnEscape]);

	useLayoutEffect(() => {
		if (!inertBackground || !open || !isOpen) return;

		const app = document.querySelector<HTMLElement>('#root > .App');
		if (!app || app.inert) return;

		app.inert = true;
		return () => {
			app.inert = false;
		};
	}, [inertBackground, open, isOpen]);

	const transitionEnded = useCallback(() => {
		if (!open) {
			setIsOpen(false);
			transitionComplete.current?.();
		} else {
			// prevent body scrolling when modal is open
			document.body.style.overflow = 'hidden';
			transitionCompletedOpen.current?.();
		}

	}, [open]);

	useEffect(() => {
		// convert to ref so we don't have to change the transitionEnded callback every render
		transitionComplete.current = onTransitionedClosed;
	}, [onTransitionedClosed]);

	useEffect(() => {
		// convert to ref so we don't have to change the transitionEnded callback every render
		transitionCompletedOpen.current = onTransitionedOpened;
	}, [onTransitionedOpened]);

	const Trap = trapFocus ? FocusTrap : React.Fragment;

	const trapProps = trapFocus ? { focusTrapOptions: inertBackground ? { ...focusTrapOptions, escapeDeactivates: false } : focusTrapOptions } : {};

	const PrimaryTooltip = (): JSX.Element => <Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12}></Icon>;

	const scrollbarVisible = useMemo(() => {
		if (modalBodyRef?.current && modalBodyRef.current.scrollHeight > modalBodyRef.current.clientHeight) {
			return true;
		} else {
			return false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalBodyRef.current?.scrollHeight, modalBodyRef.current?.clientHeight]);

	const handleBodyScroll = throttle((event: any) => {
		onBodyScroll?.(event?.target);
		if (event.target.scrollTop + event.target.clientHeight >= event.target.scrollHeight - 10) {
			onBodyScrollToBottom?.();
		}
	}, throttleTime);

	return isOpen ? (
		<ModalPortal className={`${portalClassName} ${isAdmin ? 'isAdmin' : ''}`.trim()}>
			<Trap {...trapProps}>
				<div
					className={classNames(className, "modal-wrapper", openClass, { 'dark-mode': darkMode })}
					onTransitionEnd={transitionEnded}
					style={{ pointerEvents: 'auto' }}
					id={id}
				>
					<div
						onClick={cancellable && !aDropdownIsOpen ? onRequestClose : () => ({})}
						className="modal-background"
					></div>
					<div
						role="dialog"
						aria-modal={true}
						// by default the label is the title property, but some modals have no visible title by design
						// so this can be passed to apply a label to headerless modals
						aria-label={!title && ariaLabel ? ariaLabel : undefined}
						aria-labelledby={title ? "modal-title" : undefined}
						aria-description={ariaDescription ? ariaDescription : undefined}
						className={classNames(
							"modal",
							size,
							{ 'no-padding': noPadding },
							{ 'no-side-padding': noSidePadding },
							addModalPadding ? 'modal-all-around-border-padding' : '',
							eventPreview ? 'event-preview' : '',
							isAdmin ? 'isAdmin' : ''
						)}
					>
						<ModalBodyWrapper useAdditionalWrapper={useAdditionalWrapper}>
							<>
								{hideHeader ? null : (
									<div className={classNames("modal-header", { 'pad-title': padTitle }, closeByX ? "close-by-x" : "")}>
										<div className="modal-title-container">
											<h4 className={`modal-title ${titleClassName || ''}`} id="modal-title" aria-hidden={!title || undefined}>{title}</h4>
											{closeByX &&
												<div className="closeByXIcon" onClick={onRequestClose} role="button" tabIndex={0} aria-label="close">
													<Icon name={ICONS.CLOSE} size={16} color={COLORS.WHITE} />
												</div>}
											{topRightToggle && onToggleClicked &&
												<div className="platform">
													<div className="field-group switch">
														<label>{toggleLabel}</label>
														<Switch
															on={toggleValue}
															value={toggleValue}
															onClick={onToggleClicked}
														/>
														<div className="tooltip">
															<FieldLabel
																label=" "
																required
																tooltip={[PrimaryTooltip, topRightToggleTooltipText]}
															/>
														</div>
													</div>
												</div>
											}
										</div>
										{closeable && (
											<button
												className="no-style close"
												onClick={onRequestClose}
												role="button"
												aria-label={t('Close', 'Close')}
											>
												<Icon
													name={ICONS.CLOSE}
													size={16}
													color={COLORS.WHITE}
												/>
											</button>
										)}
										{subtitle && <p className="modal-subtitle">{subtitle}</p>}
										{header}
									</div>
								)}
								<div
									ref={mergeRefs(modalBodyRef, bodyRef)}
									className={`modal-body ${scrollOverflow ? 'scroll-overflow' : ''} ${showOverflow ? 'show-overflow' : ''} ${scrollbarVisible && "scrollbar-visible"}`}
									onScroll={handleBodyScroll}
								>{children}</div>
								{footer && <div className="modal-footer">{footer}</div>}
							</>
						</ModalBodyWrapper>
					</div>
				</div>
			</Trap>
		</ModalPortal>
	) : (
		<></>
	);
});

export default ModalComponent;
